import {
  ApiDeviceSite,
  ApiManualMeasuresAddition,
  ApiReturnAllSensor,
  ApiReturnCampaign,
  ApiReturnDeviceMeasureType,
  ApiReturnDeviceType,
  ApiReturnMeasurePoint,
  ApiReturnMeasureType,
} from '@highimpact-frontend/api-types';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import { FormControl, Stack, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import assert from 'assert';
import {
  forwardRef,
  LegacyRef,
  ReactEventHandler,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api_manual_data } from '../api/api-commons';
import { getKeyFromParamsAndId } from '../store/data/slice';
import { isDataState } from '../store/data/state';
import { useAppSelector } from '../store/hooks';
import { mapFilter, toRecordBy } from '../utils/array';
import { useSubmit } from '../utils/hooks/useSubmit';
import { values } from '../utils/record';
import CustomizedDialog from './utils/MUISpecifics/dialog/CustumizedDialog';
import StateDecorator from './utils/state/StateDecorator';
import InputMultiType from './utils/MUISpecifics/inputMultiType/InputMultiType';

interface ValueFormComponentHelperProps {
  siteId: number;
  campaigns: Record<number, ApiReturnCampaign>;
  siteDevices: Record<number, ApiDeviceSite>;
  devices: Record<number, ApiReturnAllSensor>;
  deviceMeasureTypes: Record<number, ApiReturnDeviceMeasureType>;
  measureTypes: Record<number, ApiReturnMeasureType>;
  deviceTypes: Record<number, ApiReturnDeviceType>;
  measurePoints: Record<number, ApiReturnMeasurePoint>;
  onAfterInsert?: () => void;

  isOpen?: boolean;
  onCancel?: () => void;
  title?: string;
}

function oneDayBefore(endDate: string): string {
  const date = new Date(endDate);
  date.setDate(date.getDate() - 1);
  date.setHours(0, 0, 0, 0);
  return date.toISOString().split('T')[0] + 'T00:00';
}

function ValueFormComponentHelper({
  siteId,
  campaigns,
  siteDevices,
  measurePoints,
  devices,
  deviceMeasureTypes,
  measureTypes,
  deviceTypes,
  onAfterInsert,

  isOpen = false,
  onCancel,
  title,
}: ValueFormComponentHelperProps): JSX.Element {
  const inputRefs = useRef<{ [key: string]: string | null }>({});

  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(
    null
  );
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split('T')[0] + 'T00:00'
  );

  const { submit, state } = useSubmit<void>();

  const filteredDevicesWithMeasurePoint = useMemo(() => {
    if (!selectedCampaignId) return [];
    const selectedCampaign = campaigns[selectedCampaignId];
    if (!selectedCampaign) return [];

    const siteStations = Object.values(measurePoints).filter(
      (sd) => sd.site_id === siteId
    );

    return mapFilter(siteStations, (station) => {
      const deviceForStation = Object.values(devices).find(
        (d) =>
          (d.id === station.device_site?.device_id &&
            d.device_type_id &&
            deviceTypes[d.device_type_id!]?.thematic_id &&
            selectedCampaign.thematic_ids?.includes(
              deviceTypes[d.device_type_id!].thematic_id as number
            ) &&
            d.type === 'manual_sensor') ||
          d.type === 'automatic_sensor'
      );
      return deviceForStation
        ? {
            measure_point: station,
            device: deviceForStation,
          }
        : undefined;
    });
  }, [
    selectedCampaignId,
    campaigns,
    measurePoints,
    siteId,
    devices,
    deviceTypes,
  ]);

  const handleSend: ReactEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const data = mapFilter(
      filteredDevicesWithMeasurePoint,
      ({ measure_point, device }) => {
        const values = mapFilter(
          Object.values(deviceMeasureTypes).filter(
            (dmt) => dmt.device_id === device.id
          ),
          (dmt) => {
            const measureType = Object.values(measureTypes).find(
              (mt) => mt.id === dmt.measure_type_id
            );
            if (!measureType) return undefined;
            const value =
              inputRefs.current[`${device.id}-${dmt.measure_type_id}`];
            if (!value) return undefined;
            return {
              measure_type_id: measureType.id,
              datetime: selectedDate,
              value: Number(value),
            } as ApiManualMeasuresAddition['values'][number];
          }
        );
        if (values.length === 0) return undefined;
        return {
          site_id: siteId,
          measure_point_id: measure_point.id,
          device_id: device.id,
          values,
        } as ApiManualMeasuresAddition;
      }
    ).flat();

    submit({
      callbacks: [
        async () => {
          await Promise.all(data.map((datum) => api_manual_data.post(datum)));
        },
        () => {
          onAfterInsert && onAfterInsert();
          if (typeof onCancel === 'function') onCancel();
        },
      ],
      successMessage: 'Les données ont été ajoutées avec succès',
      errorMessages: {
        anyError: 'Une erreur est survenue lors de l’ajout des données',
        unicityError: 'Des doublons ont été détectés dans les données',
      },
    });
  };

  return (
    <CustomizedDialog
      open={isOpen}
      onCancel={onCancel ?? (() => {})}
      title={title}
    >
      <form>
        <Stack spacing={2}>
          <FormControl fullWidth margin="normal">
            <Autocomplete
              options={Object.values(campaigns)}
              getOptionLabel={(option) => option.name} // Displays names
              isOptionEqualToValue={(option, value) => option.id === value.id} // Ensures correct selection
              onChange={(_, newValue) => {
                const campaignId = newValue?.id;
                if (!campaignId) return;
                setSelectedCampaignId(campaignId);
                const campaign = Object.values(campaigns).find(
                  (c) => c.id === campaignId
                );
                if (campaign) setSelectedDate(oneDayBefore(campaign.end_date));
              }}
              renderInput={(params) => <TextField {...params} label="Campagne" />}
            />
          </FormControl>

          <TextField
            disabled={selectedCampaignId === null}
            label="Date"
            type="datetime-local"
            fullWidth
            margin="normal"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            slotProps={
              selectedCampaignId
                ? {
                    htmlInput: {
                      min:
                        campaigns[selectedCampaignId].start_date.split('T')[0] +
                        'T00:00',
                      max: oneDayBefore(campaigns[selectedCampaignId].end_date),
                    },
                  }
                : undefined
            }
          />

          {selectedCampaignId === null ? (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" component="div" align="center">
                  Sélectionnez une campagne pour saisir des données
                </Typography>
                {/* TODO: add manage campaign button */}
              </CardContent>
            </Card>
          ) : filteredDevicesWithMeasurePoint.length === 0 ? (
            <Card variant="outlined">
              <CardContent>
                <Typography
                  gutterBottom
                  sx={{ color: 'text.secondary', fontSize: 14 }}
                >
                  Pas de saisie possible
                </Typography>
                <Typography variant="h6" component="div" align="center">
                  Aucune station de mesure associée au thème de la campagne
                </Typography>
                {/* TODO: add manage campaign button */}
              </CardContent>
            </Card>
          ) : (
            <>
              {filteredDevicesWithMeasurePoint.map(({ device, measure_point }) =>
                Object.values(deviceMeasureTypes)
                  .filter((dmt) => dmt.device_id === device.id)
                  .map((dmt) => {
                    const measureType = Object.values(measureTypes).find(
                      (mt) => mt.id === dmt.measure_type_id
                    );
                    return (
                      <InputMultiType
                        key={`${device.id}-${dmt.measure_type_id}`}
                        name={`${measure_point.name} - ${measureType?.name} (${measureType?.unite})`}
                        type="number"
                        inputMode='decimal'
                        label={`${measure_point.name} - ${measureType?.name} (${measureType?.unite})`}
                        step={0.1} 
                        onChange={(value) =>
                          (inputRefs.current[
                            `${device.id}-${dmt.measure_type_id}`
                          ] = value)}                        
                      />
                    );
                  })
              )}

              <Box
                sx={{ m: 1, position: 'relative' }}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  onClick={handleSend}
                  disabled={state === 'Loading'}
                  endIcon={state === 'Success' ? <CheckIcon /> : <SaveIcon />}
                  loading={state === 'Loading'}
                  loadingPosition="end"
                  variant="contained"
                >
                  Enregistrer
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </form>
    </CustomizedDialog>
  );
}

interface ValueFormComponentProps {
  siteId: number;
  onAfterInsert?: () => void;
  /*Methods for useSubmit*/
  isOpen?: boolean;
  onCancel?: () => void;
  title?: string;
}

function ValueFormComponent(
  {
    siteId,
    onAfterInsert,
    /*Methods for useSubmit*/
    isOpen = false,
    onCancel,
    title,
  }: ValueFormComponentProps,
  ref: LegacyRef<HTMLDivElement>
): JSX.Element {
  const siteDevices = useAppSelector(
    (state) => state.data_slice.externals.site_devices
  );
  const devices = useAppSelector((state) => state.data_slice.externals.devices);
  const deviceMeasureTypes = useAppSelector(
    (state) => state.data_slice.externals.device_measure_types
  );
  const measureTypes = useAppSelector(
    (state) => state.data_slice.externals.measure_types
  );
  const measurePoints = useAppSelector(
    (state) => state.data_slice.externals.measure_points
  );
  const deviceTypes = useAppSelector(
    (state) => state.data_slice.externals.device_types
  );
  const campaigns = useAppSelector(
    (state) => state.data_slice.externals.campaigns
  );

  const campaignParams = { site_id: siteId };

  return StateDecorator(
    [
      siteDevices,
      devices,
      deviceMeasureTypes,
      measureTypes,
      deviceTypes,
      measurePoints,
      { params: campaignParams, ...campaigns },
    ],
    ref,
    () => {
      const campaignKey = getKeyFromParamsAndId(campaignParams, 'campaigns');
      assert(isDataState(campaigns.value[campaignKey]));
      assert(isDataState(siteDevices));
      assert(isDataState(devices));
      assert(isDataState(deviceMeasureTypes));
      assert(isDataState(deviceTypes));
      assert(isDataState(measureTypes));
      assert(isDataState(measurePoints));

      const activeStations = toRecordBy(
        values(siteDevices.data).filter((ds) => !ds.end_assignement_date),
        (ds) => ds.id
      );

      return (
        <ValueFormComponentHelper
          siteId={siteId}
          siteDevices={activeStations}
          devices={devices.data}
          deviceMeasureTypes={deviceMeasureTypes.data}
          measureTypes={measureTypes.data}
          deviceTypes={deviceTypes.data}
          campaigns={campaigns.value[campaignKey].data}
          onAfterInsert={onAfterInsert}
          measurePoints={measurePoints.data}
          /*Methods for useSubmit*/
          isOpen={isOpen}
          onCancel={onCancel}
          title={title}
        />
      );
    }
  );
}

export default forwardRef(ValueFormComponent);
